import React from 'react';
import styled from 'styled-components';
import { useSwipeable } from 'react-swipeable';
import { secondTheoryLocales } from '../../locales/firstScreen';

import { GlobalFonts } from '../../styles/GlobalFonts';
import { Footer } from '../UI/Footer/Footer';

const Container = styled.div`
  background: #151515;
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 10px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  height: 90px;
  margin: 50px auto 30px;
  padding: 13px;
  border-radius: 20px;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #0180ff;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 40px;
  font-weight: 500;
  margin-bottom: 8px;
  margin-top: 1px;
  line-height: 100%;
`;

const TheoryButton = styled.button`
  background: none;
  border: 2px solid #ffffff;
  border-radius: 12px;
  padding: 5px 7px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  line-height: 100%;
`;

const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  position: relative;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: clamp(26px, 5vw, 26px);
  font-weight: 600;
  line-height: 90%;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  white-space: pre-line;
`;

const Description = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 20px 0;
  line-height: 145%;
  letter-spacing: -0.01em;
  white-space: pre-line;

  strong {
    color: #ffffff;
    font-weight: 700;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
`;

const ScrollContainer = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overflow-x: hidden;
  padding-bottom: 100px;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 0;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0.5vh 10px 0 10px;
  width: 100%;
  flex: 1;
  justify-content: flex-end;
`;

const StyledImage = styled.img`
  width: min(100%, 300px);
  height: auto;
  object-fit: contain;
  margin: 0 auto;
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

const LANG_KEY = 'stonpools-lang';

const SecondTheory1: React.FC<{
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  toHome?: () => void;
}> = ({ onSwipeLeft, onSwipeRight, toHome }) => {
  const handlers = useSwipeable({
    onSwipedLeft: () => onSwipeLeft?.(),
    onSwipedRight: () => onSwipeRight?.(),
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    onSwipeStart: (eventData) => {
      const isVerticalSwipe =
        Math.abs(eventData.deltaY) > Math.abs(eventData.deltaX);
      if (isVerticalSwipe) {
        return false;
      }
      return true;
    },
  });

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = secondTheoryLocales[lang][1];

  const descriptionContent = React.useMemo(() => {
    if (lang === 'en' && typeof t.description === 'string') {
      const target = 'Let’s say I have token A, but I need token B.';
      const index = t.description.indexOf(target);

      if (index !== -1) {
        const before = t.description.slice(0, index);
        const after = t.description.slice(index + target.length);

        return (
          <>
            {before}
            <strong>{target}</strong>
            {after}
          </>
        );
      }
    }

    return t.description;
  }, [lang, t.description]);

  return (
    <div {...handlers}>
      <GlobalFonts />
      <Container>
        <ContentWrapper>
          <ScrollContainer>
            <MainContent>
              <HomeButton onClick={toHome}>
                <HomeButtonIcon src="/assets/homeIcon.svg" />
              </HomeButton>
              <Header>
                <Title>{t.title}</Title>
                <TheoryButton>{t.theory}</TheoryButton>
              </Header>
              <QuestionBlock>
                <QuestionText>{t.question}</QuestionText>
              </QuestionBlock>
              <Description>{descriptionContent}</Description>
              <ImageContainer>
                <StyledImage
                  src="/assets/2.1screen.svg"
                  alt="Swap illustration"
                />
              </ImageContainer>
            </MainContent>
            <Footer
              textLeft={t.back}
              textRight={t.next}
              onSwipeLeft={onSwipeLeft}
              onSwipeRight={onSwipeRight}
            ></Footer>
          </ScrollContainer>
        </ContentWrapper>
      </Container>
    </div>
  );
};

export default SecondTheory1;
